import type {
  IdpConnection,
  IdpConnectionOidc,
  IdpConnectionOidcCreateConfig,
  IdpConnectionSaml,
  IdpConnectionSamlCreateConfig,
  IdpConnectionschema,
  IdpOidcConfigschema,
  IdpSamlConfigschema,
  RoleMappingRule
} from '@app/types/sdk';

type AnyIdpConnection = (IdpConnection | IdpConnectionschema) & { type: string };

export const isOidcConnection = (connection: AnyIdpConnection | null | undefined): connection is IdpConnectionOidc => {
  return Boolean(connection && connection.type === 'oidc');
};

export const isSamlConnection = (connection: AnyIdpConnection | null | undefined): connection is IdpConnectionSaml => {
  return Boolean(connection && connection.type === 'saml');
};

export type IdpConnectionSource = 'discovery' | 'metadata' | 'manual';

export const deriveConnectionSource = (connection: AnyIdpConnection | null | undefined): IdpConnectionSource | null => {
  if (!connection) {
    return null;
  }

  if (isOidcConnection(connection)) {
    return 'discovery';
  }

  if (isSamlConnection(connection)) {
    const config = connection.config as IdpSamlConfigschema;
    if (config.idpMetadataUrl) {
      return 'metadata';
    }
    if (config.idpEntityId || config.idpCertificate || config.idpSsoUrl) {
      return 'manual';
    }
  }

  return null;
};

export const formatConnectionSourceBadge = (source: IdpConnectionSource | null): { label: string; className: string } => {
  switch (source) {
    case 'discovery':
      return { label: 'Disc.', className: 'ui-badge' };
    case 'metadata':
      return { label: 'Meta', className: 'ui-badge' };
    case 'manual':
      return { label: 'Manual', className: 'ui-badge' };
    default:
      return { label: '—', className: 'ui-badge' };
  }
};

export const formatConnectionStatusBadge = (status: string | null | undefined): { label: string; className: string } => {
  switch ((status ?? '').toLowerCase()) {
    case 'active':
      return { label: 'Active', className: 'ui-badge ui-badge--success' };
    case 'disabled':
      return { label: 'Disabled', className: 'ui-badge ui-badge--warning' };
    default:
      return { label: status ?? 'Unknown', className: 'ui-badge' };
  }
};

export interface ParsedSamlCertificate {
  pem: string;
  fingerprint: string | null;
  validFrom?: string | null;
  validTo?: string | null;
}

export interface ParsedSamlMetadata {
  entityId?: string;
  ssoUrl?: string;
  certificates: ParsedSamlCertificate[];
  nameIdFormats: string[];
}

const decodeBase64 = (value: string): Uint8Array => {
  const normalized = value.replace(/-----BEGIN CERTIFICATE-----/g, '').replace(/-----END CERTIFICATE-----/g, '').replace(/\s+/g, '');
  let binary: string;
  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    binary = window.atob(normalized);
  } else if (typeof Buffer !== 'undefined') {
    binary = Buffer.from(normalized, 'base64').toString('binary');
  } else {
    throw new Error('Base64 decoding is not supported in this environment');
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const digestSha256 = async (data: Uint8Array): Promise<string | null> => {
  try {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      const digest = await window.crypto.subtle.digest('SHA-256', data.slice().buffer);
      const bytes = new Uint8Array(digest);
      return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join(':').toUpperCase();
    }
  } catch (error) {
    console.warn('Failed to compute certificate fingerprint', error);
  }
  return null;
};

export const parseSamlMetadata = async (xml: string): Promise<ParsedSamlMetadata> => {
  const parser = new DOMParser();
  const document = parser.parseFromString(xml, 'application/xml');
  const entityDescriptor = document.querySelector('EntityDescriptor');
  if (!entityDescriptor) {
    throw new Error('Unable to locate EntityDescriptor in metadata');
  }

  const entityId = entityDescriptor.getAttribute('entityID') ?? undefined;
  const ssoService = entityDescriptor.querySelector('IDPSSODescriptor > SingleSignOnService[Binding*="HTTP-Redirect"]');
  const ssoUrl = ssoService?.getAttribute('Location') ?? undefined;

  const certificates: ParsedSamlCertificate[] = [];
  const certElements = Array.from(entityDescriptor.querySelectorAll('KeyDescriptor[use="signing"] X509Certificate'));
  for (const certElement of certElements) {
    const raw = certElement.textContent ?? '';
    const pem = raw.trim();
    if (!pem) {
      continue;
    }
    const der = decodeBase64(pem);
    const fingerprint = await digestSha256(der);
    certificates.push({ pem: `-----BEGIN CERTIFICATE-----\n${pem}\n-----END CERTIFICATE-----`, fingerprint });
  }

  const nameIdFormats = Array.from(entityDescriptor.querySelectorAll('IDPSSODescriptor > NameIDFormat'))
    .map((node) => node.textContent?.trim())
    .filter((value): value is string => Boolean(value));

  return {
    entityId,
    ssoUrl,
    certificates,
    nameIdFormats
  };
};

export interface MappingRulePreview {
  id?: string;
  description: string;
  roleSlugs: string[];
  enabled: boolean;
}

export const summarizeMappingRule = (rule: RoleMappingRule): MappingRulePreview => {
  const enabled = rule.enabled ?? true;
  const match = (rule.match ?? {}) as Record<string, any>;
  const result = (rule.result ?? {}) as Record<string, unknown>;
  const rawRoleSlugs = Array.isArray(result['roleSlugs']) ? (result['roleSlugs'] as unknown[]) : [];
  const resultRoleSlugs = rawRoleSlugs
    .filter((value): value is string => typeof value === 'string')
    .map((slug) => slug.trim())
    .filter((slug) => slug.length > 0);
  const clauses: string[] = [];

  const source = typeof match.source === 'string' ? match.source : null;
  const operator = typeof match.operator === 'string' ? match.operator : undefined;
  const value = typeof match.value === 'string' ? match.value.trim() : '';
  const path = typeof match.path === 'string' ? match.path : undefined;

  if (source && value) {
    switch (source) {
      case 'group': {
        clauses.push(`group ${operator ?? 'equals'} ${value}`);
        break;
      }
      case 'claim': {
        const claimPath = path && path.trim().length > 0 ? path : 'claim';
        clauses.push(`${claimPath} ${operator ?? 'equals'} ${value}`);
        break;
      }
      case 'attribute': {
        const attributeName = path && path.trim().length > 0 ? path : 'attribute';
        clauses.push(`${attributeName} ${operator ?? 'equals'} ${value}`);
        break;
      }
      default: {
        clauses.push(`${source} ${operator ?? 'equals'} ${value}`);
        break;
      }
    }
  }

  const description = clauses.length > 0 ? clauses.join(' · ') : 'Matches all assertions';

  return {
    id: rule.id,
    description,
    roleSlugs: resultRoleSlugs,
    enabled
  };
};

export const defaultOidcConfig = (): IdpConnectionOidcCreateConfig => ({
  issuer: '',
  clientId: '',
  clientSecretId: '',
  scopes: ['openid', 'email', 'profile'],
  enforcePkce: false,
  tokenAuthMethod: 'client_secret_basic',
  clockSkewSeconds: 120
});

export const defaultSamlConfig = (): IdpConnectionSamlCreateConfig => ({
  nameIdFormat: 'unspecified',
  allowIdpInitiated: true,
  clockSkewSeconds: 120
});
